import axios from "axios";
import { ActivationData, Onboarding, OnboardingWorkSecurityStatus } from "types/Onboarding";
import { API_URL } from "../constants";

// --- Admin CRUD ---

export async function fetchOnboardings(
  companyId: string,
  filtersValue: any
): Promise<Onboarding[]> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/fetch`,
    filtersValue
  );
  return res.data.onboardings as Onboarding[];
}

export async function fetchOnboardingById(
  companyId: string,
  onboardingId: string
): Promise<Onboarding> {
  const res = await axios.get(
    `${API_URL}/${companyId}/onboarding/${onboardingId}`
  );
  return res.data.onboarding;
}

/** Response from POST /:companyId/onboarding (full hire and contract registration). */
export interface CreateOnboardingResponse {
  onboarding: Onboarding;
  employeeUser?: { id: string; email: string; name: string };
}

export async function createOnboarding(
  companyId: string,
  data: Partial<Onboarding>
): Promise<CreateOnboardingResponse> {
  const res = await axios.post(`${API_URL}/${companyId}/onboarding`, data);
  return res.data as CreateOnboardingResponse;
}

export async function updateOnboarding(
  companyId: string,
  onboardingId: string,
  data: Partial<Onboarding>
): Promise<Onboarding> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/onboarding/${onboardingId}`,
    data
  );
  return res.data;
}

export async function deleteOnboarding(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.delete(`${API_URL}/${companyId}/onboarding/${onboardingId}`);
}

// --- Step Progression ---

export async function generateStep(
  companyId: string,
  onboardingId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/generate-step`
  );
  return res.data;
}

export async function sendForSignature(
  companyId: string,
  onboardingId: string,
  data:
    | { signMethod: "dokobit" | "manual" }
    | { signMethod: "signontab"; selectedTabUser: string }
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/send-for-signature`,
    data
  );
  return res.data;
}

/** Advance step 1 → step 2 (no signing required for step 1). */
export async function completeStep1(
  companyId: string,
  onboardingId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/complete-step-1`
  );
  return res.data;
}

/** @deprecated Use generateStep + sendForSignature instead */
export async function completeStep(
  companyId: string,
  onboardingId: string,
  data: { signMethod: "dokobit" | "manual" }
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/complete-step`,
    data
  );
  return res.data;
}

export async function uploadSignedDocument(
  companyId: string,
  onboardingId: string,
  data: { fileId: string; fileType?: string } | string
): Promise<any> {
  const payload =
    typeof data === "string"
      ? { fileId: data }
      : data;
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/upload-signed-document`,
    payload
  );
  return res.data;
}

// --- Medical Check ---

export async function approveMedical(
  companyId: string,
  onboardingId: string,
  payload: { validFrom: number; validUntil: number }
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/approve-medical`,
    payload
  );
  return res.data;
}

export async function revokeMedicalApproval(
  companyId: string,
  onboardingId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/revoke-medical-approval`
  );
  return res.data;
}

export async function reactivateOnboarding(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/reactivate`
  );
}

export async function reopenStep(
  companyId: string,
  onboardingId: string,
  step: string
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/reopen-step`,
    { step }
  );
}

export async function unlockStep(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/unlock-step`
  );
}

/**
 * HR fills the employee form (step_2) with the data they already hold. Only
 * the HR fields are stored; 409 EMPLOYEE_FORM_LOCKED once the employee has
 * submitted it.
 */
export async function prefillEmployeeForm(
  companyId: string,
  onboardingId: string,
  data: Record<string, any>
): Promise<{ onboarding: Onboarding }> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/employee-form`,
    data
  );
  return res.data;
}

export async function activateUser(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/activate-user`
  );
}

// --- Work Security step ---

export async function fetchOnboardingWorkSecurityStatus(
  companyId: string,
  onboardingId: string
): Promise<OnboardingWorkSecurityStatus> {
  const res = await axios.get(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/work-security-status`
  );
  return res.data;
}

export async function completeWorkSecurityStep(
  companyId: string,
  onboardingId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/complete-work-security`
  );
  return res.data;
}

export async function resendWorkSecuritySignature(
  companyId: string,
  onboardingId: string,
  fileId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/work-security/resend`,
    { fileId }
  );
  return res.data;
}

// --- Step 7: Activation (deprecated in v2, kept for backward compat) ---

export async function fetchActivationData(
  companyId: string,
  onboardingId: string
): Promise<ActivationData> {
  const res = await axios.get(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/activation-data`
  );
  return res.data;
}

export async function completeActivation(
  companyId: string,
  onboardingId: string,
  data: {
    // Left out by a hiring head: his activation never touches rights or folders.
    // The rights given by hand; the position's own come on top.
    permissions?: string[];
    categories?: string[];
    manager: string | null;
    departments?: string[];
  }
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/complete-activation`,
    data
  );
  return res.data;
}

export async function fetchOnboardingsByUser(
  companyId: string,
  userId: string
): Promise<any> {
  const res = await axios.get(
    `${API_URL}/${companyId}/onboarding/by-user/${userId}`
  );
  return res.data.onboardings;
}

export async function deleteStepDocument(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/step-document`
  );
}

export async function sendLoginLink(
  companyId: string,
  onboardingId: string
): Promise<void> {
  await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/send-login-link`
  );
}

export async function getKioskCode(
  companyId: string,
  onboardingId: string,
  regenerate = false
): Promise<{ kioskCode: string }> {
  const res = await axios.get(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/kiosk-code`,
    { params: regenerate ? { regenerate: "true" } : {} }
  );
  return res.data;
}

// --- Admin File Upload ---

export async function uploadOnboardingFile(
  companyId: string,
  onboardingId: string,
  fileId: string,
  fileType: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/upload-file`,
    { fileId, fileType }
  );
  return res.data;
}

export async function deleteOnboardingFile(
  companyId: string,
  onboardingId: string,
  fileId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/file/${fileId}`
  );
}

// --- Admin Section B ---

export async function updateOnboardingAdmin(
  companyId: string,
  onboardingId: string,
  data: Partial<Onboarding>
): Promise<Onboarding> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/admin`,
    data
  );
  return res.data;
}

export async function updateOnboardingDepartments(
  companyId: string,
  onboardingId: string,
  departmentIds: string[]
): Promise<{ departments: { id: string; name: string }[] }> {
  const res = await axios.patch(
    `${API_URL}/${companyId}/onboarding/${onboardingId}/departments`,
    { departmentIds }
  );
  return res.data;
}

// --- My Onboarding (Employee) ---

export interface MyOnboardingListItem {
  id: string;
  company: { id: string; name?: string; code?: string };
  status: string;
  firstName?: string;
  lastName?: string;
  hiringDate?: number | null;
  positionLT?: string;
  positionEN?: string;
  employeeSubmitted?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

export async function fetchMyOnboardings(): Promise<MyOnboardingListItem[]> {
  const res = await axios.get(`${API_URL}/my-onboarding`);
  return res.data.onboardings || [];
}

export async function fetchMyOnboarding(
  onboardingId: string
): Promise<Onboarding> {
  const res = await axios.get(`${API_URL}/my-onboarding/${onboardingId}`);
  return res.data.onboarding || res.data;
}

export async function updateMyOnboarding(
  onboardingId: string,
  data: any,
  autoSave = false
): Promise<{ saved: boolean; subStep: number; updatedAt: number }> {
  const res = await axios.patch(
    `${API_URL}/my-onboarding/${onboardingId}`,
    data,
    autoSave ? { headers: { "X-Auto-Save": "true" } } : undefined
  );
  return res.data;
}

export async function uploadMyOnboardingFile(
  onboardingId: string,
  file: File,
  fileType: string
): Promise<any> {
  // Step 1: upload file
  const formData = new FormData();
  formData.append("file", file);
  const uploadRes = await axios.post(
    `${API_URL}/my-onboarding/${onboardingId}/upload`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  const fileId = uploadRes.data;

  // Step 2: link file to onboarding
  const linkRes = await axios.post(
    `${API_URL}/my-onboarding/${onboardingId}/link-file`,
    { fileId, fileType }
  );
  return linkRes.data;
}

export async function deleteMyOnboardingFile(
  onboardingId: string,
  fileId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/my-onboarding/${onboardingId}/file/${fileId}`
  );
}

// --- My Onboarding Children ---

export async function addMyOnboardingChild(
  onboardingId: string,
  data: {
    firstName: string;
    lastName: string;
    personalCode?: string;
  }
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/my-onboarding/${onboardingId}/children`,
    data
  );
  return res.data;
}

export async function updateMyOnboardingChild(
  onboardingId: string,
  childId: string,
  data: { firstName?: string; lastName?: string; personalCode?: string }
): Promise<any> {
  const res = await axios.patch(
    `${API_URL}/my-onboarding/${onboardingId}/children/${childId}`,
    data
  );
  return res.data;
}

export async function deleteMyOnboardingChild(
  onboardingId: string,
  childId: string
): Promise<void> {
  await axios.delete(
    `${API_URL}/my-onboarding/${onboardingId}/children/${childId}`
  );
}

// --- Kiosk ---

export async function verifyKiosk(data: {
  personalEmail: string;
  kioskCode: string;
}): Promise<any> {
  const res = await axios.post(
    `${API_URL}/onboarding/kiosk/verify`,
    data
  );
  return res.data;
}

export async function submitKiosk(data: any): Promise<any> {
  const res = await axios.patch(
    `${API_URL}/onboarding/kiosk/submit`,
    data
  );
  return res.data;
}

export async function sendSignatureReminder(
  companyId: string,
  signatureId: string
): Promise<any> {
  const res = await axios.post(
    `${API_URL}/${companyId}/signatures/${signatureId}/send-reminder`
  );
  return res.data;
}
